import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { P, match } from 'ts-pattern';
import { userSelector } from '../../../../../../selectors';
import { usePlanDetails } from '../../../../../../hooks/usePlanDetails';
import { PotentialOfferingPeriodAction } from '../../../../../../selectors/interfaces';
import useGetUserStatesInPeriods from '../../../../../../hooks/use-get-user-states-in-enrollment/use-get-user-states-in-periods';
import useGetPeriodsForChanges from '../../hooks/use-get-periods-for-changes';
import {
  useGetPeriodsDatesInfo,
  useGetPeriodsOpenInfo,
} from '../../../../../../hooks/use-get-periods-info';

enum ChangeDirection {
  Both = 'both',
  Up = 'up',
  Down = 'down',
}

export enum ContributionChangeDirection {
  IncreaseOrDecrease = 'IncreaseOrDecrease',
  Increase = 'Increase',
  Decrease = 'Decrease',
  Denied = 'Denied',
}

export enum InitiallyAvailableNumberOfContributionChanges {
  Undefined,
  One,
  Many,
}

export enum ChangeCurrentContributionContentState {
  DoNotShow = 'DoNotShow',
  ShowForIncreasesOrDecreases = 'ShowForIncreasesOrDecreases',
  ShowForIncreasesOnly = 'ShowForIncreasesOnly',
  ShowForDecreasesOnly = 'ShowForDecreasesOnly',
  ShowForUnlimitedChanges = 'ShowForUnlimitedChanges',
  ShowForWaitForNextEnrollment = 'ShowForWaitForNextEnrollment',
  ShowForWaitForOverlapOfferingPeriod = 'ShowForWaitForOverlapOfferingPeriod',
  ShowForWaitForNextPurchasePeriod = 'ShowForWaitForNextPurchasePeriod',
  ShowForWaitForPurchasePeriodStart = 'ShowForWaitForPurchasePeriodStart',
  ShowForResetChangeWindow = 'ShowForResetChangeWindow',
}

const useGetContributionChangesInfoDuringOfferingPeriod = () => {
  const {
    changesDuringOffering: {
      changeDirection,
      changesFrequencyAnyDirection,
      remainingChangesAnyDirection,
      changesFrequencyUp,
      remainingChangesUp,
      changesFrequencyDown,
      remainingChangesDown,
      isInResetChangeWindow,
      cashlessChangeAllowed,
    },
    currentOpAllowedActions,
    currentOpPotentiallyAllowedActions,
  } = useSelector(userSelector);
  const { userStateInCurrentPeriodOpOrPp } = useGetUserStatesInPeriods();
  const planDetails = usePlanDetails();

  const getInitiallyAvailableNumberOfChanges = useCallback(
    (remaining?: number | null, frequency?: number | null) => {
      if (remaining !== null && frequency) {
        return frequency === 1
          ? InitiallyAvailableNumberOfContributionChanges.One
          : InitiallyAvailableNumberOfContributionChanges.Many;
      }

      return InitiallyAvailableNumberOfContributionChanges.Undefined;
    },
    [],
  );

  const initiallyAvailableNumberOfAnyChanges = useMemo(
    () =>
      getInitiallyAvailableNumberOfChanges(
        remainingChangesAnyDirection,
        changesFrequencyAnyDirection,
      ),
    [
      remainingChangesAnyDirection,
      changesFrequencyAnyDirection,
      getInitiallyAvailableNumberOfChanges,
    ],
  );

  const initiallyAvailableNumberOfUpChanges = useMemo(
    () =>
      getInitiallyAvailableNumberOfChanges(
        remainingChangesUp,
        changesFrequencyUp,
      ),
    [
      remainingChangesUp,
      changesFrequencyUp,
      getInitiallyAvailableNumberOfChanges,
    ],
  );

  const initiallyAvailableNumberOfDownChanges = useMemo(
    () =>
      getInitiallyAvailableNumberOfChanges(
        remainingChangesDown,
        changesFrequencyDown,
      ),
    [
      remainingChangesDown,
      changesFrequencyDown,
      getInitiallyAvailableNumberOfChanges,
    ],
  );

  const initialChangesDirection = useMemo(
    () =>
      match(changeDirection)
        .with(ChangeDirection.Up, () => ContributionChangeDirection.Increase)
        .with(ChangeDirection.Down, () => ContributionChangeDirection.Decrease)
        .otherwise(() => ContributionChangeDirection.IncreaseOrDecrease),
    [changeDirection],
  );

  const areChangesEverAllowed = useMemo(
    () =>
      currentOpPotentiallyAllowedActions?.includes(
        PotentialOfferingPeriodAction.UpdateElectionAction,
      ) ?? false,
    [currentOpPotentiallyAllowedActions],
  );

  const unlimitedContributionChangesAllowed = useMemo(
    () =>
      currentOpAllowedActions?.includes(
        PotentialOfferingPeriodAction.UpdateElectionAction,
      ) &&
      changesFrequencyUp === null &&
      changesFrequencyDown === null &&
      changesFrequencyAnyDirection === null,
    [
      currentOpAllowedActions,
      changesFrequencyUp,
      changesFrequencyDown,
      changesFrequencyAnyDirection,
    ],
  );

  const getAllowedDirectionChanges = useCallback(
    (minAllowedPercent: number) =>
      match({
        changeDirection,
        remainingChangesAnyDirection,
        changesFrequencyDown,
        remainingChangesDown,
        changesFrequencyUp,
        remainingChangesUp,
        unlimitedContributionChangesAllowed,
      })
        .with(
          {
            unlimitedContributionChangesAllowed: true,
            changeDirection: ChangeDirection.Both,
          },
          () => ContributionChangeDirection.IncreaseOrDecrease,
        )
        .with(
          {
            unlimitedContributionChangesAllowed: true,
            changeDirection: ChangeDirection.Up,
          },
          () => ContributionChangeDirection.Increase,
        )
        .with(
          {
            unlimitedContributionChangesAllowed: true,
            changeDirection: ChangeDirection.Down,
          },
          () => ContributionChangeDirection.Decrease,
        )
        // No changes if a user used all of allowed changes
        .with(
          {
            changeDirection: ChangeDirection.Down,
            remainingChangesDown: 0,
          },
          {
            changeDirection: ChangeDirection.Up,
            remainingChangesUp: 0,
          },
          {
            // changeDirection: both can either track up and down changes independently,
            // or it can use any-direction changes. So we have to do this:
            changeDirection: 'both',
            remainingChangesUp: P.union(0, null),
            remainingChangesDown: P.union(0, null),
            remainingChangesAnyDirection: P.union(0, null),
          },
          () => ContributionChangeDirection.Denied,
        )
        // The above cases already addressed where no changes remain. So the below
        // only handles cases where some changes still remain:
        .with(
          {
            changeDirection: ChangeDirection.Down,
          },
          {
            changeDirection: ChangeDirection.Both,
            changesFrequencyUp: P.not(null),
            remainingChangesUp: 0,
          },
          // No changes if a user who enrolled min % is in a plan that only allows for decreases
          () =>
            (Number(userStateInCurrentPeriodOpOrPp.contributionPercentage) <=
            minAllowedPercent
              ? ContributionChangeDirection.Denied
              : ContributionChangeDirection.Decrease),
        )
        .with(
          {
            changeDirection: ChangeDirection.Up,
          },
          {
            changeDirection: ChangeDirection.Both,
            changesFrequencyDown: P.not(null),
            remainingChangesDown: 0,
          },
          // No changes if a user who enrolled max % is in a plan that only allows for decreases
          () =>
            (planDetails?.enrollMinMax?.max &&
            Number(userStateInCurrentPeriodOpOrPp.contributionPercentage) >=
              planDetails.enrollMinMax.max
              ? ContributionChangeDirection.Denied
              : ContributionChangeDirection.Increase),
        )
        .otherwise(() => ContributionChangeDirection.IncreaseOrDecrease),
    [
      changeDirection,
      remainingChangesAnyDirection,
      changesFrequencyDown,
      remainingChangesDown,
      changesFrequencyUp,
      remainingChangesUp,
      unlimitedContributionChangesAllowed,
      planDetails?.enrollMinMax?.max,
      userStateInCurrentPeriodOpOrPp.contributionPercentage,
    ],
  );

  const allowedDirectionForContributionChange = useMemo(
    () => getAllowedDirectionChanges(planDetails?.enrollMinMax?.min ?? 1),
    [getAllowedDirectionChanges, planDetails?.enrollMinMax?.min],
  );

  const allowedDirectionForSuspensionContent = useMemo(
    () => getAllowedDirectionChanges(0),
    [getAllowedDirectionChanges],
  );

  return {
    initiallyAvailableNumberOfAnyChanges,
    initiallyAvailableNumberOfUpChanges,
    initiallyAvailableNumberOfDownChanges,
    initialChangesDirection,
    unlimitedContributionChangesAllowed,
    allowedDirectionForContributionChange,
    allowedDirectionForSuspensionContent,
    isInResetChangeWindow,
    cashlessChangeAllowed,
    areChangesEverAllowed,
  };
};

export const useGetContributionChangesInfo = () => {
  const { suspended: suspendedFromCurrentOP } = useSelector(userSelector);
  const { isEnrollmentOpen, isQuietPeriodBetweenEnrollmentAndOffering } =
    useGetPeriodsOpenInfo();
  const { currentOfferingDates, futurePurchaseDates } =
    useGetPeriodsDatesInfo();
  const { userStateInCurrentPeriodOpOrPp, userStateInClosestPeriodOpOrPp } =
    useGetUserStatesInPeriods();
  const contributionChangesDuringOfferingPeriod =
    useGetContributionChangesInfoDuringOfferingPeriod();

  const {
    currentPeriodTypeForChanges,
    currentPeriodAllowedActions,
    currentOpAllowedActions,
    futurePeriodTypeForChanges,
    futurePeriodAllowedActions,
  } = useGetPeriodsForChanges();

  const noMoreContributionChangesForCurrent = useMemo(() => {
    const isWindowForContributionChangesOpen =
      currentPeriodAllowedActions.includes(
        PotentialOfferingPeriodAction.UpdateElectionAction,
      ) ||
      currentPeriodAllowedActions.includes(
        PotentialOfferingPeriodAction.ScheduledEnrollmentAction,
      ) ||
      (suspendedFromCurrentOP &&
        currentPeriodAllowedActions.includes(
          PotentialOfferingPeriodAction.ResumeElectionAction,
        ));

    return (
      // No changes when the window to make changes is closed
      !isWindowForContributionChangesOpen ||
      contributionChangesDuringOfferingPeriod.allowedDirectionForContributionChange ===
        ContributionChangeDirection.Denied
    );
  }, [
    contributionChangesDuringOfferingPeriod.allowedDirectionForContributionChange,
    currentPeriodAllowedActions,
    suspendedFromCurrentOP,
  ]);

  const allowedChangeDirectionForCurrent = useMemo(() => {
    if (noMoreContributionChangesForCurrent) {
      return ContributionChangeDirection.Denied;
    }

    return contributionChangesDuringOfferingPeriod.allowedDirectionForContributionChange;
  }, [
    noMoreContributionChangesForCurrent,
    contributionChangesDuringOfferingPeriod.allowedDirectionForContributionChange,
  ]);

  const changeContributionContentStateForCurrent = useMemo(() => {
    if (
      (!isQuietPeriodBetweenEnrollmentAndOffering &&
        !userStateInCurrentPeriodOpOrPp.enrolledEspp) ||
      (isQuietPeriodBetweenEnrollmentAndOffering &&
        !userStateInClosestPeriodOpOrPp.enrolledEspp)
    ) {
      return ChangeCurrentContributionContentState.DoNotShow;
    }

    if (contributionChangesDuringOfferingPeriod.isInResetChangeWindow) {
      return ChangeCurrentContributionContentState.ShowForResetChangeWindow;
    }

    if (noMoreContributionChangesForCurrent) {
      if (isQuietPeriodBetweenEnrollmentAndOffering) {
        return ChangeCurrentContributionContentState.ShowForWaitForPurchasePeriodStart;
      }

      if (
        currentOfferingDates &&
        futurePurchaseDates &&
        futurePurchaseDates.end <= currentOfferingDates.end &&
        currentOpAllowedActions?.includes(
          PotentialOfferingPeriodAction.UpdateElectionAction,
        )
      ) {
        return ChangeCurrentContributionContentState.ShowForWaitForNextPurchasePeriod;
      }

      return isEnrollmentOpen
        ? ChangeCurrentContributionContentState.ShowForWaitForOverlapOfferingPeriod
        : ChangeCurrentContributionContentState.ShowForWaitForNextEnrollment;
    }

    return match({
      changeDirection:
        contributionChangesDuringOfferingPeriod.initialChangesDirection,
      unlimitedChanges:
        contributionChangesDuringOfferingPeriod.unlimitedContributionChangesAllowed,
    })
      .with(
        { unlimitedChanges: true },
        () => ChangeCurrentContributionContentState.ShowForUnlimitedChanges,
      )
      .with(
        { changeDirection: ContributionChangeDirection.IncreaseOrDecrease },
        () => ChangeCurrentContributionContentState.ShowForIncreasesOrDecreases,
      )
      .with(
        { changeDirection: ContributionChangeDirection.Increase },
        () => ChangeCurrentContributionContentState.ShowForIncreasesOnly,
      )
      .with(
        { changeDirection: ContributionChangeDirection.Decrease },
        () => ChangeCurrentContributionContentState.ShowForDecreasesOnly,
      )
      .otherwise(
        () =>
          ChangeCurrentContributionContentState.ShowForWaitForNextEnrollment,
      );
  }, [
    contributionChangesDuringOfferingPeriod.isInResetChangeWindow,
    contributionChangesDuringOfferingPeriod.initialChangesDirection,
    contributionChangesDuringOfferingPeriod.unlimitedContributionChangesAllowed,
    noMoreContributionChangesForCurrent,
    isEnrollmentOpen,
    currentOfferingDates,
    currentOpAllowedActions,
    futurePurchaseDates,
    userStateInCurrentPeriodOpOrPp.enrolledEspp,
    userStateInClosestPeriodOpOrPp.enrolledEspp,
    isQuietPeriodBetweenEnrollmentAndOffering,
  ]);

  return useMemo(
    () => ({
      currentPeriodTypeForChanges,
      futurePeriodTypeForChanges,
      noMoreContributionChangesForCurrent,
      cashlessChangeAllowedForCurrent:
        contributionChangesDuringOfferingPeriod.cashlessChangeAllowed,
      allowedChangeDirectionForCurrent,
      changeContributionContentStateForCurrent,
      initiallyAvailableNumberOfAnyChanges:
        contributionChangesDuringOfferingPeriod.initiallyAvailableNumberOfAnyChanges,
      initiallyAvailableNumberOfUpChanges:
        contributionChangesDuringOfferingPeriod.initiallyAvailableNumberOfUpChanges,
      initiallyAvailableNumberOfDownChanges:
        contributionChangesDuringOfferingPeriod.initiallyAvailableNumberOfDownChanges,
      showChangeFutureContribution: !!futurePeriodAllowedActions.some(
        (allowedAction) =>
          allowedAction ===
            PotentialOfferingPeriodAction.UpdateElectionAction ||
          allowedAction ===
            PotentialOfferingPeriodAction.ScheduledEnrollmentAction,
      ),
      isInResetChangeWindow:
        contributionChangesDuringOfferingPeriod.isInResetChangeWindow,
      areChangesToCurrentPeriodEverAllowed:
        contributionChangesDuringOfferingPeriod.areChangesEverAllowed,
    }),
    [
      noMoreContributionChangesForCurrent,
      contributionChangesDuringOfferingPeriod.cashlessChangeAllowed,
      contributionChangesDuringOfferingPeriod.initiallyAvailableNumberOfAnyChanges,
      contributionChangesDuringOfferingPeriod.initiallyAvailableNumberOfUpChanges,
      contributionChangesDuringOfferingPeriod.initiallyAvailableNumberOfDownChanges,
      contributionChangesDuringOfferingPeriod.isInResetChangeWindow,
      contributionChangesDuringOfferingPeriod.areChangesEverAllowed,
      allowedChangeDirectionForCurrent,
      changeContributionContentStateForCurrent,
      currentPeriodTypeForChanges,
      futurePeriodTypeForChanges,
      futurePeriodAllowedActions,
    ],
  );
};
